/* eslint-disable jsx-a11y/label-has-associated-control, react/no-unescaped-entities */
import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { postRequest } from 'apis/ApiInstence';
import axiosInstance from 'apis/axiosConfig';

const normalizeAgreementPath = (path) => {
    if (!path) return null;
    const cleanPath = String(path).trim().replace(/^\/+/, '');
    if (/^(https?:|file:|content:|banner\/)/i.test(cleanPath)) return cleanPath;
    return `banner/${cleanPath}`;
};

const buildAgreementDocumentUrl = (path) => {
    if (!path) return '#';
    const cleanPath = String(path).trim();
    if (/^(https?:|file:|content:)/i.test(cleanPath)) return cleanPath;
    const backendUrl = (process.env.REACT_APP_BACKEND || '').replace(/\/+$/, '');
    return `${backendUrl}/${cleanPath.replace(/^\/+/, '')}`;
};

const AddUser = () => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [isDeclared, setIsDeclared] = useState(false);
    const [agreementPdfUrl, setAgreementPdfUrl] = useState(null);
    const [uploadingAgreement, setUploadingAgreement] = useState(false);

    // Form State matching mobile app structure exactly, plus image storage keys
    const [formData, setFormData] = useState({
        loanCategory: '', // Now handled by dropdown
        userRole: 'User',
        firstName: '',
        lastName: '',
        fatherName: '',
        motherName: '',
        phone: '',
        dob: '',
        email: '',
        city: '',
        state: '',
        zipCode: '',
        // Step 2
        aadhar: '',
        pan: '',
        aadharImageBase64: '', // Base64 uploaded Aadhar image preview
        panImageBase64: '',    // Base64 uploaded PAN image preview
        accountHolderName: '',
        bankName: '',
        bankAccount: '',
        ifscCode: '',
        occupation: '',
        companyName: '',
        income: '',
        address: '',
        // Step 3
        loanAmount: '',
        interestRate: '',
        emiFrequency: 'Monthly', // Daily, Monthly, Yearly
        specificCollectionPoint: '1',
        durationValue: '12',
        collectionStartDate: '',
        remarks: '',
        investmentType: 'One-Time',
        loan_provider_id: ''
    });

    const [activeCategories, setActiveCategories] = useState([]);
    const [loanProviders, setLoanProviders] = useState([]);
    const userRole = localStorage.getItem('role') || 'superAdmin';

    useEffect(() => {
        const fetchLoanProviders = async () => {
            if (userRole === 'superAdmin') {
                try {
                    const res = await axiosInstance.get('loan-providers');
                    if (res.data && Array.isArray(res.data)) {
                        setLoanProviders(res.data);
                    }
                } catch (err) {
                    console.error("Failed to fetch loan providers", err);
                }
            }
        };
        fetchLoanProviders();
    }, [userRole]);


    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await axiosInstance.get('loan-categories/active');
                if (res.data && res.data.length > 0) {
                    setActiveCategories(res.data);
                    // Automatically select first category on load to prevent blank submission
                    setFormData(prev => ({ ...prev, loanCategory: res.data[0].name }));
                }
            } catch (err) {
                console.error("Failed to fetch active categories", err);
            }
        };
        fetchCategories();
    }, []);

    const handleChange = (e) => {
        let { name, value } = e.target;
        
        // Strict live input sanitization
        if (['firstName', 'lastName', 'fatherName', 'motherName', 'city', 'state', 'accountHolderName', 'bankName', 'occupation'].includes(name)) {
            // Only allow letters and spaces
            value = value.replace(/[^A-Za-z\s]/g, '');
        } else if (['phone', 'aadhar', 'bankAccount', 'zipCode', 'durationValue', 'loanAmount', 'income'].includes(name)) {
            // Only allow numbers
            value = value.replace(/[^0-9]/g, '');
        } else if (['pan', 'ifscCode'].includes(name)) {
            // Only allow alphanumeric uppercase
            value = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        } else if (['interestRate'].includes(name)) {
            // Positive numbers and decimals only
            value = value.replace(/[^0-9.]/g, '');
            // Prevent multiple decimals
            if ((value.match(/\./g) || []).length > 1) {
                value = value.slice(0, -1);
            }
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileUpload = (e, fieldName) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                toast.error("Please upload a valid image file (JPG, PNG)!");
                return;
            }
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;
                    
                    // Cap max dimension to 1024px to ensure optimized transmission and avoid packet size drops
                    const maxDim = 1024;
                    if (width > height) {
                        if (width > maxDim) {
                            height = Math.round((height * maxDim) / width);
                            width = maxDim;
                        }
                    } else {
                        if (height > maxDim) {
                            width = Math.round((width * maxDim) / height);
                            height = maxDim;
                        }
                    }
                    
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    
                    // Output highly compressed Web/JPEG Base64 string (~100KB)
                    const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
                    setFormData(prev => ({ ...prev, [fieldName]: compressedBase64 }));
                    toast.success(`${fieldName === 'aadharImageBase64' ? 'Aadhar' : 'PAN'} Document Photo Attached & Compressed Perfectly!`);
                };
                img.src = event.target.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const removeUploadedImage = (fieldName) => {
        setFormData(prev => ({ ...prev, [fieldName]: '' }));
    };

    const handleFrequency = (freq) => {
        setFormData(prev => ({ 
            ...prev, 
            emiFrequency: freq,
            durationValue: freq === 'Daily' ? '365' : freq === 'Monthly' ? '12' : freq === 'Yearly' ? '1' : freq === 'Flexible' ? '1' : '12'
        }));
    };

    // EMI/Investment Calculation matching mobile app logic exactly
    const calculation = useMemo(() => {
        const P = parseFloat(formData.loanAmount) || 0;
        const interestPercent = parseFloat(formData.interestRate) || 0;
        const N = parseInt(formData.durationValue) || 1;

        if (formData.userRole === 'Investor') {
            let years = 1;
            if (formData.emiFrequency === 'Daily') {
                years = N / 365;
            } else if (formData.emiFrequency === 'Monthly') {
                years = N / 12;
            } else {
                years = N;
            }

            if (formData.investmentType === 'One-Time') {
                // One-time investment: P is Total Lump Sum. Total return at maturity is P + Simple Interest.
                const totalInterest = P * (interestPercent / 100) * years;
                const total = P + totalInterest;
                return { emi: Math.round(total), total: Math.round(total) }; // Return total maturity payout
            } else {
                // Recurring investment: P is Total Target Investment. Total return at maturity is P + Simple Interest.
                const totalInterest = P * (interestPercent / 100) * years;
                const total = P + totalInterest;
                const emi = N > 0 ? (P / N) : 0;
                return { emi: Math.round(emi), total: Math.round(total) }; // regular deposit installment is P / N, maturity return is total
            }
        } else {
            // Flexible / Khata calculation
            if (formData.emiFrequency === 'Flexible') {
                const oneMonthInterest = P * (interestPercent / 100);
                const total = P + oneMonthInterest;
                return { emi: Math.round(total), total: Math.round(total) };
            }

            // Standard Reducing/EMI calculation for regular borrowers
            const annualRate = interestPercent / 100;
            let R;

            if (formData.emiFrequency === 'Daily') {
                R = annualRate / 365;
            } else if (formData.emiFrequency === 'Monthly') {
                R = annualRate / 12;
            } else {
                R = annualRate;
            }

            if (P === 0 || R === 0) return { emi: 0, total: 0 };
            const emi = (P * R * Math.pow(1 + R, N)) / (Math.pow(1 + R, N) - 1);
            return { emi: Math.round(emi), total: Math.round(emi * N) };
        }
    }, [formData.loanAmount, formData.interestRate, formData.durationValue, formData.emiFrequency, formData.userRole, formData.investmentType]);

    const handleDownloadAgreement = () => {
        const isInvestor = formData.userRole === 'Investor';
        const docTitle = isInvestor ? 'INVESTMENT PARTNERSHIP AGREEMENT' : 'LOAN AGREEMENT & REPAYMENT BOND';
        const firstPartyRole = isInvestor ? 'INVESTOR' : 'BORROWER';
        const secondPartyRole = 'LOAN PROVIDER';

        const agreementText = isInvestor
          ? `This Agreement governs the investment terms between the Investor and the Loan Provider. The Investor agrees to deposit the capital specified below under the selected terms. The Loan Provider agrees to pay back the principal along with accumulated interest as per the agreed payout schedule. Any default on payouts will attract additional interest liabilities as per standard financial guidelines.`
          : `The Borrower hereby acknowledges receipt of the principal loan amount and agrees to repay the same with interest in accordance with the EMI payment schedule detailed above. In case of any default in payment of any EMI installment, the entire remaining balance shall become immediately payable, and the Loan Provider reserves the right to initiate legal proceedings under appropriate financial regulations.`;

        const principalVal = parseFloat(formData.loanAmount) || 0;
        const interestRateVal = formData.interestRate || '0';
        const emiVal = calculation.emi || 0;
        const totalVal = calculation.total || 0;
        const tenureText = `${formData.durationValue} ${formData.emiFrequency === 'Daily' ? 'Days' : formData.emiFrequency === 'Monthly' ? 'Months' : 'Years'}`;

        const htmlContent = `
          <html>
            <head>
              <title>${docTitle}</title>
              <style>
                @media print {
                  body { margin: 0; padding: 20px; }
                  .no-print { display: none; }
                }
                body {
                  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
                  color: #1E293B;
                  margin: 0;
                  padding: 40px;
                  background-color: #fff;
                }
                .header {
                  text-align: center;
                  border-bottom: 3px double #0A8EA0;
                  padding-bottom: 20px;
                  margin-bottom: 30px;
                }
                .logo {
                  font-size: 26px;
                  font-weight: 800;
                  color: #041C32;
                  letter-spacing: 1px;
                  margin: 0;
                  text-transform: uppercase;
                }
                .subtitle {
                  font-size: 12px;
                  color: #64748B;
                  margin-top: 5px;
                  text-transform: uppercase;
                  letter-spacing: 2px;
                }
                .doc-title {
                  text-align: center;
                  font-size: 16px;
                  font-weight: 700;
                  text-transform: uppercase;
                  margin-bottom: 30px;
                  color: #0A8EA0;
                  border: 1px solid #E2E8F0;
                  padding: 12px;
                  background-color: #F8FAFC;
                  border-radius: 8px;
                  letter-spacing: 1px;
                }
                .section-title {
                  font-size: 12px;
                  font-weight: 700;
                  color: #041C32;
                  border-bottom: 1px solid #CBD5E1;
                  padding-bottom: 5px;
                  margin-top: 25px;
                  margin-bottom: 15px;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                }
                table {
                  width: 100%;
                  border-collapse: collapse;
                  margin-bottom: 20px;
                }
                th, td {
                  padding: 8px 10px;
                  font-size: 11px;
                  text-align: left;
                  border: 1px solid #E2E8F0;
                }
                tr:nth-child(even) {
                  background-color: #F8FAFC;
                }
                td.label {
                  font-weight: 600;
                  color: #64748B;
                  width: 30%;
                  background-color: #F1F5F9;
                }
                td.value {
                  color: #0F172A;
                }
                .terms-box {
                  border: 1px solid #E2E8F0;
                  border-radius: 8px;
                  padding: 15px;
                  background-color: #F8FAFC;
                  margin-top: 20px;
                }
                .terms-title {
                  font-size: 12px;
                  font-weight: 700;
                  color: #0F172A;
                  margin-bottom: 8px;
                  text-transform: uppercase;
                }
                .terms-text {
                  font-size: 10px;
                  color: #475569;
                  line-height: 1.6;
                  text-align: justify;
                  margin: 0;
                }
                .signatures {
                  display: flex;
                  justify-content: space-between;
                  margin-top: 60px;
                  page-break-inside: avoid;
                }
                .sig-box {
                  width: 45%;
                  border-top: 1px dashed #64748B;
                  text-align: center;
                  padding-top: 8px;
                  font-size: 11px;
                  font-weight: 600;
                  color: #475569;
                  margin-top: 50px;
                }
                .footer-note {
                  text-align: center;
                  font-size: 9px;
                  color: #94A3B8;
                  margin-top: 50px;
                  border-top: 1px solid #F1F5F9;
                  padding-top: 10px;
                }
              </style>
            </head>
            <body>
              <div class="header">
                <h1 class="logo">ALL ROUNDER FINANCE</h1>
                <div class="subtitle">Secure Financial Ledger & Credit Services</div>
              </div>

              <div class="doc-title">${docTitle}</div>

              <div class="section-title">Party Identification</div>
              <table>
                <tr>
                  <td class="label">First Party (${firstPartyRole})</td>
                  <td class="value">${formData.firstName} ${formData.lastName}</td>
                </tr>
                <tr>
                  <td class="label">Father's Name</td>
                  <td class="value">${formData.fatherName || 'N/A'}</td>
                </tr>
                <tr>
                  <td class="label">Mother's Name</td>
                  <td class="value">${formData.motherName || 'N/A'}</td>
                </tr>
                <tr>
                  <td class="label">Contact / Phone</td>
                  <td class="value">${formData.phone || 'N/A'}</td>
                </tr>
                <tr>
                  <td class="label">Email ID</td>
                  <td class="value">${formData.email || 'N/A'}</td>
                </tr>
                <tr>
                  <td class="label">Residential Address</td>
                  <td class="value">${formData.address || 'N/A'}, ${formData.city || 'N/A'}, ${formData.state || 'N/A'} - ${formData.zipCode || 'N/A'}</td>
                </tr>
              </table>

              <div class="section-title">KYC & Banking Information</div>
              <table>
                <tr>
                  <td class="label">Aadhaar Number</td>
                  <td class="value">${formData.aadhar || 'N/A'}</td>
                  <td class="label">PAN Number</td>
                  <td class="value">${formData.pan || 'N/A'}</td>
                </tr>
                <tr>
                  <td class="label">Bank Name</td>
                  <td class="value">${formData.bankName || 'N/A'}</td>
                  <td class="label">Account Holder</td>
                  <td class="value">${formData.accountHolderName || 'N/A'}</td>
                </tr>
                <tr>
                  <td class="label">Account Number</td>
                  <td class="value">${formData.bankAccount || 'N/A'}</td>
                  <td class="label">IFSC Code</td>
                  <td class="value">${formData.ifscCode || 'N/A'}</td>
                </tr>
              </table>

              <div class="section-title">Financial Transaction Details</div>
              <table>
                <tr>
                  <td class="label">Transaction Category</td>
                  <td class="value">${formData.loanCategory || 'Standard'}</td>
                  <td class="label">Principal Amount</td>
                  <td class="value">INR ${principalVal.toLocaleString('en-IN')}/-</td>
                </tr>
                <tr>
                  <td class="label">Interest Rate</td>
                  <td class="value">${interestRateVal}% P.A.</td>
                  <td class="label">Interval Mode</td>
                  <td class="value">${formData.emiFrequency}</td>
                </tr>
                <tr>
                  <td class="label">Duration / Tenure</td>
                  <td class="value">${tenureText}</td>
                  <td class="label">Start / Commencement Date</td>
                  <td class="value">${formData.collectionStartDate || new Date().toISOString().split('T')[0]}</td>
                </tr>
                <tr>
                  <td class="label">Repayment Installment (EMI)</td>
                  <td class="value">INR ${emiVal.toLocaleString('en-IN')}/-</td>
                  <td class="label">Total Maturity Value</td>
                  <td class="value">INR ${totalVal.toLocaleString('en-IN')}/-</td>
                </tr>
              </table>

              <div class="terms-box">
                <div class="terms-title">Standard Terms & Undertakings</div>
                <p class="terms-text">${agreementText}</p>
              </div>

              <div class="signatures">
                <div class="sig-box">Signature of ${firstPartyRole}</div>
                <div class="sig-box">Authorized Signature of ${secondPartyRole}</div>
              </div>

              <div class="footer-note">
                This agreement is system-generated on ${new Date().toLocaleDateString('en-IN')} via All Rounder Finance Platform and is legally binding upon validation.
              </div>

              <script>
                window.onload = function() {
                  window.print();
                  setTimeout(function() { window.close(); }, 500);
                };
              </script>
            </body>
          </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();
    }

    const handleUploadAgreement = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf' && !file.type.startsWith('image/')) {
            toast.error("Please upload a valid PDF or Image file!");
            return;
        }

        setUploadingAgreement(true);
        const payload = new FormData();
        payload.append('pdf', file);

        try {
            const res = await axiosInstance.post('customer/upload', payload, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            if (res.data && res.data.fileUrl) {
                setAgreementPdfUrl(normalizeAgreementPath(res.data.fileUrl));
                toast.success("Signed Agreement uploaded & attached successfully!");
            } else {
                toast.error("Failed to receive file URL from server.");
            }
        } catch (err) {
            console.error("Error uploading agreement PDF:", err);
            toast.error(err.response?.data?.message || "Failed to upload signed agreement!");
        } finally {
            setUploadingAgreement(false);
        }
    };

    const validateStep = () => {
        if (step === 1) {
            if (!formData.firstName) { toast.error("First Name is required!"); return false; }
            if (formData.firstName.length < 2) { toast.error("First Name must be at least 2 characters!"); return false; }
            if (!formData.phone || formData.phone.length !== 10) { toast.error("Please enter a valid 10-digit Mobile Number!"); return false; }
            if (!formData.dob) { toast.error("Date of Birth is required!"); return false; }
            if (formData.email && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
                toast.error("Please enter a valid Email Address!"); return false;
            }
        }
        else if (step === 2) {
            if (!formData.aadhar || formData.aadhar.length !== 12) { toast.error("Aadhar Number must be exactly 12 digits!"); return false; }
            if (!formData.pan || !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan)) { toast.error("Invalid PAN Card Number format! (e.g. ABCDE1234F)"); return false; }
            if (!formData.bankAccount || formData.bankAccount.length < 9) { toast.error("Please enter a valid Bank Account Number!"); return false; }
            if (formData.ifscCode && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode)) { toast.error("Invalid IFSC Code format!"); return false; }
            // Documents
            if (!formData.aadharImageBase64) { toast.error("Please attach Aadhar Card Photo!"); return false; }
            if (!formData.panImageBase64) { toast.error("Please attach PAN Card Photo!"); return false; }
        }
        else if (step === 3) {
            if (formData.userRole !== 'Investor' && !formData.loanCategory) { toast.error("Please select a Loan Category!"); return false; }
            if (!formData.loanAmount || parseFloat(formData.loanAmount) <= 0) { toast.error(`Please enter a valid ${formData.userRole === 'Investor' ? 'Investment' : 'Loan'} Amount!`); return false; }
            if (!formData.interestRate || parseFloat(formData.interestRate) <= 0) { toast.error("Please enter a valid Interest Rate!"); return false; }
            if (!formData.durationValue || parseInt(formData.durationValue) <= 0) { toast.error("Duration/Tenure must be greater than 0!"); return false; }
        }
        
        return true;
    };

    const handleNext = () => {
        if (validateStep()) {
            setStep(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handlePrev = () => {
        setStep(prev => prev - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleResetForm = () => {
        setFormData({
            loanCategory: 'Individual',
            firstName: '',
            lastName: '',
            fatherName: '',
            motherName: '',
            phone: '',
            dob: '',
            email: '',
            city: '',
            state: '',
            zipCode: '',
            aadhar: '',
            pan: '',
            aadharImageBase64: '',
            panImageBase64: '',
            accountHolderName: '',
            bankName: '',
            bankAccount: '',
            ifscCode: '',
            occupation: '',
            companyName: '',
            income: '',
            address: '',
            loanAmount: '',
            interestRate: '',
            emiFrequency: 'Monthly',
            specificCollectionPoint: '1',
            durationValue: '12',
            collectionStartDate: '',
            remarks: '',
            investmentType: 'One-Time'
        });
        setIsDeclared(false);
        setAgreementPdfUrl(null);
        setUploadingAgreement(false);
        setStep(1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Added provider selection dropdown and loan_provider_id handling
const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isDeclared) {
            toast.error("Please verify and check the declaration box before submitting!");
            return;
        }

        setLoading(true);
        try {
            // Step 1: Create Customer
            let formattedPassword = '123456';
            if (formData.dob) {
                const parts = formData.dob.split('-');
                if (parts.length === 3) {
                    formattedPassword = `${parts[2]}${parts[1]}${parts[0]}`; // Convert YYYY-MM-DD to DDMMYYYY
                } else {
                    formattedPassword = formData.dob.replace(/[^0-9]/g, '');
                }
            }
            if (!formattedPassword) formattedPassword = 'password123';

            const customerPayload = {
                name: formData.firstName,
                last_name: formData.lastName,
                father_name: formData.fatherName,
                mother_name: formData.motherName,
                mobile: formData.phone,
                email: formData.email,
                dob: formData.dob,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                zip_code: formData.zipCode,
                password: formattedPassword,
                pan_number: formData.pan,
                aadhaar_number: formData.aadhar,
                bank_account_number: formData.bankAccount,
                ifsc_code: formData.ifscCode,
                bank_name: formData.bankName,
                account_holder_name: formData.accountHolderName || `${formData.firstName} ${formData.lastName}`.trim(),
                monthly_income: parseFloat(formData.income) || 0,
                loan_category: formData.userRole === 'Investor' ? 'Investor' : formData.loanCategory,
                user_type: formData.userRole.toLowerCase(),
                uploaded_documents: JSON.stringify({
                    aadhar: formData.aadharImageBase64 || null,
                    pan: formData.panImageBase64 || null,
                    agreement: agreementPdfUrl || null
                }),
                is_active: 1,
                loan_provider_id: userRole === 'superAdmin' ? (formData.loan_provider_id || null) : null
            };

            const customerRes = await postRequest('customer', customerPayload);
            
            // Explicitly detect if backend returned an error response
            if (customerRes?.status >= 400 || customerRes?.data?.message || customerRes?.data?.error) {
                let errorMsg = customerRes?.data?.message || customerRes?.data?.error;
                if (!errorMsg && customerRes?.data) {
                    errorMsg = typeof customerRes.data === 'string' ? customerRes.data : JSON.stringify(customerRes.data);
                }
                throw new Error(errorMsg || "Registration failed due to backend database error.");
            }

            const customerId = customerRes?.data?.id || customerRes?.id;

            if (!customerId) {
                throw new Error("Failed to retrieve Customer ID from server response.");
            }

            // Step 2 is now combined with Step 1 directly into the Customer ledger

            // Step 3: Create Loan Data
            const isInvestor = formData.userRole === 'Investor';
            const isOneTime = isInvestor && formData.investmentType === 'One-Time';

            let tenureMonths = parseInt(formData.durationValue) || 12;
            if (formData.emiFrequency === 'Yearly') tenureMonths = tenureMonths * 12;
            if (formData.emiFrequency === 'Daily') tenureMonths = Math.ceil(tenureMonths / 30);

            const loanPayload = {
                loan_user_id: customerId,
                loan_amount: parseFloat(formData.loanAmount) || 0,
                interest_rate: parseFloat(formData.interestRate) || 0,
                loan_tenure_months: tenureMonths,
                monthly_emi: calculation.emi,
                total_loan_pay: calculation.total,
                emi_paid_count: 0,
                remaining_emi_count: isOneTime ? 1 : (parseInt(formData.durationValue) || 12),
                remaining_amount: calculation.total,
                loan_status: 'active',
                remarks: formData.remarks,
                loan_start_date: formData.collectionStartDate || new Date().toISOString().split('T')[0],
                emi_frequency: isOneTime ? 'Maturity' : (formData.emiFrequency || 'Monthly'),
                total_tenure_count: isOneTime ? 1 : (parseInt(formData.durationValue) || 12),
                loan_provider_id: userRole === 'superAdmin' ? (formData.loan_provider_id || null) : null
            };
            const loanRes = await postRequest('loans-data', loanPayload);
            if (loanRes?.status >= 400 || loanRes?.data?.message || loanRes?.data?.error) {
                throw new Error(loanRes?.data?.message || loanRes?.data?.error || "Failed to configure Loan setup parameters.");
            }

            toast.success("User Onboarded Successfully! Profile created in core ledger.");
            
            // Advance to completed Step 5 confirmation screen to satisfy requirement of applying Checkmark to Step 4
            setIsDeclared(false);
            setStep(5);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            toast.error(error.message || "Submission failed. Please try again.");
            console.error("Error creating profile:", error);
        } finally {
            setLoading(false);
        }
    };

    // Shared label style to absolutely guarantee visibility and prevent CSS theme white-text override
    const labelStyle = {
        color: '#0F172A',
        fontSize: '13px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '8px',
        display: 'block'
    };

    // Detail display label for review screen
    const reviewLabel = {
        fontSize: '12px',
        color: '#64748B',
        fontWeight: '600',
        textTransform: 'uppercase',
        marginBottom: '2px',
        display: 'block'
    };

    const reviewValue = {
        fontSize: '15px',
        color: '#0F172A',
        fontWeight: '700',
        wordBreak: 'break-word'
    };

    return (
        <div className="container-fluid mt-4 mb-5" style={{ fontFamily: "'Inter', 'Roboto', sans-serif" }}>
            {/* Embedded scoped custom styles for maximum visual excellence */}
            <style dangerouslySetInnerHTML={{__html: `
                .premium-card {
                    background: #ffffff;
                    border-radius: 20px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
                    border: 1px solid rgba(226, 232, 240, 0.8);
                    transition: all 0.3s ease;
                }
                .premium-input {
                    background-color: #F8FAFC !important;
                    border: 1.5px solid #E2E8F0 !important;
                    border-radius: 12px !important;
                    padding: 12px 16px !important;
                    font-size: 15px !important;
                    color: #1E293B !important;
                    font-weight: 500 !important;
                    transition: all 0.2s ease !important;
                    box-shadow: none !important;
                }
                .premium-input:focus {
                    background-color: #ffffff !important;
                    border-color: #0284C7 !important;
                    box-shadow: 0 0 0 4px rgba(2, 132, 199, 0.12) !important;
                }
                .upload-box {
                    border: 2px dashed #CBD5E1;
                    border-radius: 16px;
                    padding: 24px;
                    text-align: center;
                    background: #F8FAFC;
                    transition: all 0.2s;
                    cursor: pointer;
                    position: relative;
                }
                .upload-box:hover {
                    border-color: #0284C7;
                    background: #F0F9FF;
                }
                .img-preview-card {
                    width: 100%;
                    max-height: 200px;
                    object-fit: contain;
                    border-radius: 12px;
                    border: 1px solid #E2E8F0;
                    background: #ffffff;
                    padding: 4px;
                }
                .pill-btn {
                    border-radius: 12px;
                    font-weight: 700;
                    padding: 12px 24px;
                    transition: all 0.25s ease;
                    border: 1.5px solid #E2E8F0;
                }
                .pill-btn.active {
                    background: linear-gradient(135deg, #0284C7 0%, #0369A1 100%);
                    color: white !important;
                    border-color: transparent;
                    box-shadow: 0 4px 12px rgba(2, 132, 199, 0.25);
                }
                .pill-btn.inactive {
                    background: #F8FAFC;
                    color: #64748B !important;
                }
                .pill-btn.inactive:hover {
                    background: #F1F5F9;
                    color: #0F172A !important;
                }
                .step-circle {
                    width: 48px;
                    height: 48px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 16px;
                    transition: all 0.4s ease;
                }
                .step-circle.active {
                    background: linear-gradient(135deg, #0284C7 0%, #0369A1 100%);
                    color: white;
                    box-shadow: 0 0 0 6px rgba(2, 132, 199, 0.15);
                }
                .step-circle.completed {
                    background: #10B981;
                    color: white;
                }
                .step-circle.pending {
                    background: #F1F5F9;
                    color: #94A3B8;
                    border: 2px solid #E2E8F0;
                }
                .review-section {
                    background: #F8FAFC;
                    border-radius: 16px;
                    padding: 20px;
                    border: 1px solid #E2E8F0;
                    margin-bottom: 20px;
                }
                .section-badge {
                    display: inline-block;
                    padding: 6px 14px;
                    border-radius: 8px;
                    font-weight: 800;
                    font-size: 12px;
                    margin-bottom: 16px;
                    text-transform: uppercase;
                }
            `}} />

            {/* Premium Page Title */}
            <div className="mb-4 px-2">
                <span className="badge badge-primary px-3 py-1 mb-2 font-weight-bold" style={{ borderRadius: '8px', backgroundColor: '#E0F2FE', color: '#0369A1' }}>
                    ONBOARDING SYSTEM
                </span>
                <h2 className="font-weight-bold" style={{ color: '#0F172A', letterSpacing: '-0.5px' }}>
                    {formData.userRole === 'Investor' ? 'New Investor Profile Setup' : 'New Borrower Profile Setup'}
                </h2>
                <p style={{ color: '#64748B', fontSize: '15px' }}>
                    Fill out the verified attributes below to create an instantly active {formData.userRole === 'Investor' ? 'investment' : 'loan'} ledger
                </p>
            </div>

            {/* Stepper Header Card */}
            <div className="premium-card mb-4 p-4">
                <div className="position-relative mx-lg-5 px-lg-5 my-2">
                    {/* Background line */}
                    <div className="position-absolute" style={{ top: '22px', left: '15%', right: '15%', height: '4px', backgroundColor: '#E2E8F0', zIndex: 1 }} />
                    {/* Active progress line */}
                    <div className="position-absolute" style={{ top: '22px', left: '15%', width: `${((step - 1) / 3) * 70}%`, height: '4px', background: 'linear-gradient(90deg, #0284C7 0%, #10B981 100%)', zIndex: 2, transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }} />

                    <div className="d-flex justify-content-between position-relative" style={{ zIndex: 3 }}>
                        {[
                            { num: 1, label: 'Personal Info' },
                            { num: 2, label: 'KYC & Bank' },
                            { num: 3, label: formData.userRole === 'Investor' ? 'Invest Config' : 'Loan Config' },
                            { num: 4, label: 'Final Review' }
                        ].map((item) => {
                            const isCompleted = step > item.num;
                            const isActive = step === item.num;
                            return (
                                <div key={item.num} className="text-center" style={{ width: '100px' }}>
                                    <div className={`step-circle mx-auto mb-2 ${isCompleted ? 'completed' : isActive ? 'active' : 'pending'}`}>
                                        {isCompleted ? '✓' : item.num}
                                    </div>
                                    <span style={{ fontSize: '12px', fontWeight: isActive ? '800' : '600', color: isActive ? '#0284C7' : isCompleted ? '#10B981' : '#94A3B8', display: 'block' }}>
                                        {item.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Main Form Content Container */}
            <div className="premium-card p-4 p-md-5 bg-white">
                <form onSubmit={handleSubmit}>
                    
                    {/* STEP 1: Personal Details */}
                    {step === 1 && (
                        <div className="step-container animate__animated animate__fadeIn">
                            <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                                <div className="rounded p-2 mr-3" style={{ backgroundColor: '#E0F2FE', color: '#0284C7' }}>
                                    <span style={{ fontSize: '20px' }}>👤</span>
                                </div>
                                <div>
                                    <h4 className="font-weight-bold mb-0" style={{ color: '#0F172A' }}>
                                        {formData.userRole === 'Investor' ? 'Investor Category & Personal Identity' : 'Borrower Category & Personal Identity'}
                                    </h4>
                                    <span style={{ fontSize: '13px', color: '#64748B' }}>Step 1 of 4 mandatory stages</span>
                                </div>
                            </div>

                            {/* Role Selector */}
                            <div className="mb-4 pb-2">
                                <label style={labelStyle}>Users Role</label>
                                <div className="d-flex flex-wrap gap-3" style={{ gap: '12px' }}>
                                    <button 
                                        type="button" 
                                        className={`btn pill-btn flex-fill ${formData.userRole === 'User' ? 'active' : 'inactive'}`}
                                        onClick={() => setFormData(prev => ({ ...prev, userRole: 'User' }))}
                                    >
                                        👤 User
                                    </button>
                                    <button 
                                        type="button" 
                                        className={`btn pill-btn flex-fill ${formData.userRole === 'Investor' ? 'active' : 'inactive'}`}
                                        onClick={() => setFormData(prev => ({ ...prev, userRole: 'Investor' }))}
                                    >
                                        🏢 Investor
                                    </button>
                                </div>
                            </div>

                            {/* Input Grid */}
                            <div className="row">
                                <div className="col-md-6 mb-4">
                                    <label htmlFor="first_name" style={labelStyle}>First Name <span className="text-danger">*</span></label>
                                    <input type="text" id="first_name" name="firstName" className="form-control premium-input" value={formData.firstName} onChange={handleChange} placeholder="Enter full legal first name" required />
                                </div>
                                <div className="col-md-6 mb-4">
                                    <label htmlFor="last_name" style={labelStyle}>Last Name</label>
                                    <input type="text" id="last_name" name="lastName" className="form-control premium-input" value={formData.lastName} onChange={handleChange} placeholder="Enter surname" />
                                </div>
                                <div className="col-md-6 mb-4">
                                    <label htmlFor="father_name" style={labelStyle}>Father&apos;s Full Name</label>
                                    <input type="text" id="father_name" name="fatherName" className="form-control premium-input" value={formData.fatherName} onChange={handleChange} placeholder="Guardian/Father&apos;s authorized name" />
                                </div>
                                <div className="col-md-6 mb-4">
                                    <label htmlFor="mother_name" style={labelStyle}>Mother&apos;s Full Name</label>
                                    <input type="text" id="mother_name" name="motherName" className="form-control premium-input" value={formData.motherName} onChange={handleChange} placeholder="Mother&apos;s maiden name" />
                                </div>
                                <div className="col-md-6 mb-4">
                                    <label htmlFor="phone_num" style={labelStyle}>Mobile Number <span className="text-danger">*</span></label>
                                    <input type="tel" id="phone_num" name="phone" maxLength="10" className="form-control premium-input" value={formData.phone} onChange={handleChange} placeholder="10-digit primary number" required />
                                </div>
                                <div className="col-md-6 mb-4">
                                    <label htmlFor="dob_val" style={labelStyle}>Date of Birth <span className="text-danger">*</span></label>
                                    <input type="date" id="dob_val" name="dob" className="form-control premium-input" value={formData.dob} onChange={handleChange} required />
                                </div>
                                <div className="col-md-12 mb-4">
                                    <label htmlFor="email_id" style={labelStyle}>Email Address</label>
                                    <input type="email" id="email_id" name="email" className="form-control premium-input" value={formData.email} onChange={handleChange} placeholder="client@domain.com" />
                                </div>
                                <div className="col-md-4 mb-4">
                                    <label htmlFor="city_name" style={labelStyle}>City</label>
                                    <input type="text" id="city_name" name="city" className="form-control premium-input" value={formData.city} onChange={handleChange} placeholder="Current City" />
                                </div>
                                <div className="col-md-4 mb-4">
                                    <label htmlFor="state_name" style={labelStyle}>State Region</label>
                                    <input type="text" id="state_name" name="state" className="form-control premium-input" value={formData.state} onChange={handleChange} placeholder="State" />
                                </div>
                                <div className="col-md-4 mb-4">
                                    <label htmlFor="zip_code" style={labelStyle}>Postal Zip Code</label>
                                    <input type="text" id="zip_code" name="zipCode" maxLength="6" className="form-control premium-input" value={formData.zipCode} onChange={handleChange} placeholder="6-digit pincode" />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: KYC, Document Uploads & Bank Info */}
                    {step === 2 && (
                        <div className="step-container animate__animated animate__fadeIn">
                            <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                                <div className="rounded p-2 mr-3" style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}>
                                    <span style={{ fontSize: '20px' }}>🛡️</span>
                                </div>
                                <div>
                                    <h4 className="font-weight-bold mb-0" style={{ color: '#0F172A' }}>Government KYC Keys & Document Image Attachments</h4>
                                    <span style={{ fontSize: '13px', color: '#64748B' }}>Step 2 of 4 mandatory stages</span>
                                </div>
                            </div>

                            <div className="row">
                                {/* AADHAR CARD SECTION */}
                                <div className="col-md-6 mb-4 pb-3 border-right">
                                    <label htmlFor="aadhar_no" style={labelStyle}>Aadhar Card Number <span className="text-danger">*</span></label>
                                    <input type="text" id="aadhar_no" name="aadhar" maxLength="12" className="form-control premium-input mb-3" value={formData.aadhar} onChange={handleChange} placeholder="12-digit Unique Identity Number" required />
                                    
                                    {/* Aadhar Photo Attachment Box */}
                                    <label style={{ ...labelStyle, fontSize: '12px', color: '#0284C7' }}>📸 Attached Aadhar Card Photo</label>
                                    {!formData.aadharImageBase64 ? (
                                        <div className="upload-box mt-1">
                                            <input 
                                                type="file" 
                                                id="uploadAadharImg" 
                                                accept="image/*" 
                                                className="position-absolute" 
                                                style={{ top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                                onChange={(e) => handleFileUpload(e, 'aadharImageBase64')}
                                            />
                                            <span style={{ fontSize: '28px', display: 'block' }}>📁</span>
                                            <span className="font-weight-bold mt-2" style={{ color: '#0F172A', fontSize: '14px', display: 'block' }}>Click to Browse Aadhar Photo</span>
                                            <span style={{ fontSize: '11px', color: '#94A3B8' }}>Supports standard formats (JPG, PNG)</span>
                                        </div>
                                    ) : (
                                        <div className="text-center p-2 rounded bg-light border">
                                            <img src={formData.aadharImageBase64} alt="Aadhar Preview" className="img-preview-card mb-2" />
                                            <button 
                                                type="button" 
                                                className="btn btn-sm btn-outline-danger font-weight-bold px-3 py-1 mt-1" 
                                                style={{ borderRadius: '8px', fontSize: '12px' }}
                                                onClick={() => removeUploadedImage('aadharImageBase64')}
                                            >
                                                🗑️ Remove Attached Aadhar Photo
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* PAN CARD SECTION */}
                                <div className="col-md-6 mb-4 pb-3">
                                    <label htmlFor="pan_no" style={labelStyle}>PAN Card Number <span className="text-danger">*</span></label>
                                    <input type="text" id="pan_no" name="pan" maxLength="10" className="form-control premium-input text-uppercase mb-3" value={formData.pan} onChange={handleChange} placeholder="ABCDE1234F" required />
                                    
                                    {/* PAN Photo Attachment Box */}
                                    <label style={{ ...labelStyle, fontSize: '12px', color: '#0284C7' }}>📸 Attached PAN Card Photo</label>
                                    {!formData.panImageBase64 ? (
                                        <div className="upload-box mt-1">
                                            <input 
                                                type="file" 
                                                id="uploadPanImg" 
                                                accept="image/*" 
                                                className="position-absolute" 
                                                style={{ top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                                onChange={(e) => handleFileUpload(e, 'panImageBase64')}
                                            />
                                            <span style={{ fontSize: '28px', display: 'block' }}>💳</span>
                                            <span className="font-weight-bold mt-2" style={{ color: '#0F172A', fontSize: '14px', display: 'block' }}>Click to Browse PAN Card Photo</span>
                                            <span style={{ fontSize: '11px', color: '#94A3B8' }}>Supports standard formats (JPG, PNG)</span>
                                        </div>
                                    ) : (
                                        <div className="text-center p-2 rounded bg-light border">
                                            <img src={formData.panImageBase64} alt="PAN Preview" className="img-preview-card mb-2" />
                                            <button 
                                                type="button" 
                                                className="btn btn-sm btn-outline-danger font-weight-bold px-3 py-1 mt-1" 
                                                style={{ borderRadius: '8px', fontSize: '12px' }}
                                                onClick={() => removeUploadedImage('panImageBase64')}
                                            >
                                                🗑️ Remove Attached PAN Photo
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="col-12 mt-2 mb-3">
                                    <div className="p-3 rounded bg-light border-left border-primary" style={{ borderLeftWidth: '4px !important' }}>
                                        <span className="font-weight-bold" style={{ color: '#0369A1', fontSize: '14px' }}>🏦 Core Bank Remittance Destination</span>
                                    </div>
                                </div>

                                <div className="col-md-6 mb-4">
                                    <label htmlFor="acc_holder" style={labelStyle}>Bank Account Holder Name</label>
                                    <input type="text" id="acc_holder" name="accountHolderName" className="form-control premium-input" value={formData.accountHolderName} onChange={handleChange} placeholder="Exactly as written on passbook" />
                                </div>
                                <div className="col-md-6 mb-4">
                                    <label htmlFor="bank_name" style={labelStyle}>Bank Institution Name</label>
                                    <input type="text" id="bank_name" name="bankName" className="form-control premium-input" value={formData.bankName} onChange={handleChange} placeholder="e.g. HDFC Bank, SBI" />
                                </div>
                                <div className="col-md-6 mb-4">
                                    <label htmlFor="acc_num" style={labelStyle}>Bank Account Number <span className="text-danger">*</span></label>
                                    <input type="text" id="acc_num" name="bankAccount" className="form-control premium-input" value={formData.bankAccount} onChange={handleChange} placeholder="Enter full active account number" required />
                                </div>
                                <div className="col-md-6 mb-4">
                                    <label htmlFor="ifsc_code" style={labelStyle}>IFSC Routing Code</label>
                                    <input type="text" id="ifsc_code" name="ifscCode" className="form-control premium-input text-uppercase" value={formData.ifscCode} onChange={handleChange} placeholder="SBIN0001234" />
                                </div>

                                <div className="col-12 mt-2 mb-3">
                                    <div className="p-3 rounded bg-light border-left border-warning" style={{ borderLeftWidth: '4px !important' }}>
                                        <span className="font-weight-bold" style={{ color: '#B45309', fontSize: '14px' }}>💼 Occupation & Income Verification</span>
                                    </div>
                                </div>

                                <div className="col-md-4 mb-4">
                                    <label htmlFor="job_occ" style={labelStyle}>Occupation / Job Sector</label>
                                    <input type="text" id="job_occ" name="occupation" className="form-control premium-input" value={formData.occupation} onChange={handleChange} placeholder="Service / Business / Self-Employed" />
                                </div>
                                <div className="col-md-4 mb-4">
                                    <label htmlFor="comp_name" style={labelStyle}>Company / Workplace Name</label>
                                    <input type="text" id="comp_name" name="companyName" className="form-control premium-input" value={formData.companyName} onChange={handleChange} placeholder="Employer setup" />
                                </div>
                                <div className="col-md-4 mb-4">
                                    <label htmlFor="month_inc" style={labelStyle}>Verified Monthly Income (₹)</label>
                                    <input type="number" id="month_inc" name="income" className="form-control premium-input" value={formData.income} onChange={handleChange} placeholder="Monthly take-home" />
                                </div>

                                <div className="col-12 mb-2">
                                    <label htmlFor="res_addr" style={labelStyle}>Full Residential Address</label>
                                    <textarea id="res_addr" name="address" className="form-control premium-input" rows="3" value={formData.address} onChange={handleChange} placeholder="House number, street, nearby landmarks..."></textarea>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 3: Loan Configuration */}
                    {step === 3 && (
                        <div className="step-container animate__animated animate__fadeIn">
                            <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                                <div className="rounded p-2 mr-3" style={{ backgroundColor: '#D1FAE5', color: '#059669' }}>
                                    <span style={{ fontSize: '20px' }}>💰</span>
                                </div>
                                <div>
                                    <h4 className="font-weight-bold mb-0" style={{ color: '#0F172A' }}>
                                        {formData.userRole === 'Investor' ? 'Principal Capital & Investment Configuration Engine' : 'Principal Capital & EMI Repayment Engine'}
                                    </h4>
                                    <span style={{ fontSize: '13px', color: '#64748B' }}>Step 3 of 4 mandatory stages</span>
                                </div>
                            </div>

                            <div className="row">
                                {formData.userRole === 'Investor' && (
                                    <div className="col-md-12 mb-4 animate__animated animate__fadeIn">
                                        <label style={labelStyle}>Investment Style <span className="text-danger">*</span></label>
                                        <div className="d-flex" style={{ gap: '15px' }}>
                                            <button
                                                type="button"
                                                className="btn flex-fill py-3 font-weight-bold shadow-sm transition-all"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        investmentType: 'One-Time',
                                                        emiFrequency: 'Yearly',
                                                        durationValue: '1'
                                                    }));
                                                }}
                                                style={{
                                                    borderRadius: '12px',
                                                    fontSize: '15px',
                                                    backgroundColor: formData.investmentType === 'One-Time' ? '#7C3AED' : '#F8FAFC',
                                                    color: formData.investmentType === 'One-Time' ? '#FFFFFF' : '#475569',
                                                    border: `2px solid ${formData.investmentType === 'One-Time' ? '#7C3AED' : '#E2E8F0'}`,
                                                    transition: 'all 0.2s ease-in-out'
                                                }}
                                            >
                                                💰 Lump Sum (One-Time)
                                            </button>
                                            <button
                                                type="button"
                                                className="btn flex-fill py-3 font-weight-bold shadow-sm transition-all"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        investmentType: 'Recurring',
                                                        emiFrequency: 'Monthly',
                                                        durationValue: '12'
                                                    }));
                                                }}
                                                style={{
                                                    borderRadius: '12px',
                                                    fontSize: '15px',
                                                    backgroundColor: formData.investmentType === 'Recurring' ? '#7C3AED' : '#F8FAFC',
                                                    color: formData.investmentType === 'Recurring' ? '#FFFFFF' : '#475569',
                                                    border: `2px solid ${formData.investmentType === 'Recurring' ? '#7C3AED' : '#E2E8F0'}`,
                                                    transition: 'all 0.2s ease-in-out'
                                                }}
                                            >
                                                📅 Recurring (Installments)
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {/* Dynamic Category Dropdown */}
                                {formData.userRole !== 'Investor' && (
                                    <div className="col-md-12 mb-4">
                                        <label htmlFor="loan_category" style={labelStyle}>Category <span className="text-danger">*</span></label>
                                        <select 
                                            id="loan_category" 
                                            name="loanCategory" 
                                            className="form-control premium-input font-weight-bold" 
                                            style={{ color: '#0F172A', cursor: 'pointer', height: '55px', padding: '0 15px' }}
                                            value={formData.loanCategory} 
                                            onChange={handleChange}
                                            required
                                        >
                                            {activeCategories.length > 0 ? (
                                                activeCategories.map((cat, idx) => (
                                                    <option key={idx} value={cat.name} style={{ fontWeight: '600' }}>{cat.name}</option>
                                                ))
                                            ) : (
                                                <option value="">No active categories available</option>
                                            )}
                                        </select>
                                    </div>
                                )}

                                {/* Super Admin Loan Provider Selection */}
                                {userRole === 'superAdmin' && (
                                    <div className="col-md-12 mb-4">
                                        <label htmlFor="loan_provider_id" style={labelStyle}>Loan Provider Association</label>
                                        <select 
                                            id="loan_provider_id" 
                                            name="loan_provider_id" 
                                            className="form-control premium-input font-weight-bold" 
                                            style={{ color: '#0F172A', cursor: 'pointer', height: '55px', padding: '0 15px' }}
                                            value={formData.loan_provider_id} 
                                            onChange={handleChange}
                                        >
                                            <option value="">None (Super Admin Managed)</option>
                                            {loanProviders.map((provider, idx) => (
                                                <option key={idx} value={provider.id} style={{ fontWeight: '600' }}>
                                                    {provider.name} ({provider.email})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className="col-md-6 mb-4">
                                    <label htmlFor="loan_amt" style={labelStyle}>
                                        {formData.userRole === 'Investor' ? 'Investment Capital Amount' : 'Principal Disbursement Amount'} (₹) <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative">
                                        <span className="position-absolute font-weight-bold" style={{ left: '16px', top: '14px', color: '#0284C7', fontSize: '16px' }}>₹</span>
                                        <input type="number" id="loan_amt" name="loanAmount" className="form-control premium-input" style={{ paddingLeft: '35px !important' }} value={formData.loanAmount} onChange={handleChange} placeholder="e.g. 50000" required />
                                    </div>
                                </div>

                                <div className="col-md-6 mb-4">
                                    <label htmlFor="interest_rate" style={labelStyle}>
                                        {formData.userRole === 'Investor' ? 'Annual Interest Payout Rate' : 'Annual Base Interest Rate'} (%) <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative">
                                        <input type="number" step="0.01" id="interest_rate" name="interestRate" className="form-control premium-input" style={{ paddingRight: '40px !important' }} value={formData.interestRate} onChange={handleChange} placeholder="e.g. 12.5" required />
                                        <span className="position-absolute font-weight-bold" style={{ right: '16px', top: '14px', color: '#0284C7', fontSize: '16px' }}>%</span>
                                    </div>
                                </div>

                                {/* Frequency options */}
                                <div className="col-md-12 mb-4">
                                    <label style={labelStyle}>
                                        {formData.userRole === 'Investor' 
                                            ? (formData.investmentType === 'One-Time' ? 'Maturity Tenure Unit' : 'Payout / Deposit Interval Mode') 
                                            : 'Repayment Interval Mode'}
                                    </label>
                                    <div className="d-flex flex-wrap gap-2" style={{ gap: '10px' }}>
                                        {formData.userRole === 'Investor' ? (
                                            formData.investmentType === 'One-Time' ? (
                                                [
                                                    { key: 'Daily', label: '⚡ Days' },
                                                    { key: 'Monthly', label: '📅 Months' },
                                                    { key: 'Yearly', label: '🗓️ Years' }
                                                ].map((item) => (
                                                    <button 
                                                        key={item.key}
                                                        type="button" 
                                                        className={`btn pill-btn flex-fill ${formData.emiFrequency === item.key ? 'active' : 'inactive'}`}
                                                        onClick={() => handleFrequency(item.key)}
                                                    >
                                                        {item.label}
                                                    </button>
                                                ))
                                            ) : (
                                                [
                                                    { key: 'Daily', label: '⚡ Daily Collection' },
                                                    { key: 'Monthly', label: '📅 Monthly Payout' },
                                                    { key: 'Yearly', label: '🗓️ Yearly Payout' }
                                                ].map((item) => (
                                                    <button 
                                                        key={item.key}
                                                        type="button" 
                                                        className={`btn pill-btn flex-fill ${formData.emiFrequency === item.key ? 'active' : 'inactive'}`}
                                                        onClick={() => handleFrequency(item.key)}
                                                    >
                                                        {item.label}
                                                    </button>
                                                ))
                                            )
                                        ) : (
                                            [
                                                { key: 'Daily', label: '⚡ Daily Collection' },
                                                { key: 'Monthly', label: '📅 Monthly Schedule' },
                                                { key: 'Yearly', label: '🗓️ Yearly Lump-sum' },
                                                { key: 'Flexible', label: '♾️ Flexible Khata' }
                                            ].map((item) => (
                                                <button 
                                                    key={item.key}
                                                    type="button" 
                                                    className={`btn pill-btn flex-fill ${formData.emiFrequency === item.key ? 'active' : 'inactive'}`}
                                                    onClick={() => handleFrequency(item.key)}
                                                >
                                                    {item.label}
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {formData.emiFrequency !== 'Flexible' && (
                                    <div className="col-md-6 mb-4">
                                        <label htmlFor="tenure_val" style={labelStyle}>
                                            {formData.userRole === 'Investor' 
                                                ? `Investment Duration (${formData.emiFrequency === 'Daily' ? 'Days' : formData.emiFrequency === 'Monthly' ? 'Months' : 'Years'})`
                                                : `Total Collection Tenure (${formData.emiFrequency === 'Daily' ? 'Days' : formData.emiFrequency === 'Monthly' ? 'Months' : 'Years'})`} <span className="text-danger">*</span>
                                        </label>
                                        <input type="number" id="tenure_val" name="durationValue" className="form-control premium-input font-weight-bold" style={{ color: '#0284C7 !important', fontSize: '18px !important' }} value={formData.durationValue} onChange={handleChange} required />
                                    </div>
                                )}

                                <div className="col-md-6 mb-4">
                                    <label htmlFor="start_dt" style={labelStyle}>{formData.userRole === 'Investor' ? 'Investment Commencement Date' : 'Schedule Commencement Date'}</label>
                                    <input type="date" id="start_dt" name="collectionStartDate" className="form-control premium-input" value={formData.collectionStartDate} onChange={handleChange} />
                                </div>

                                {/* Dynamic Auto-Calculator Banner */}
                                {calculation.total > 0 && (
                                    <div className="col-12 mt-2 mb-4 animate__animated animate__fadeIn">
                                        <div 
                                            className="p-4 rounded-xl" 
                                            style={{ 
                                                background: formData.userRole === 'Investor' 
                                                    ? 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' 
                                                    : 'linear-gradient(135deg, #10B981 0%, #059669 100%)', 
                                                borderRadius: '16px', 
                                                color: 'white', 
                                                boxShadow: formData.userRole === 'Investor'
                                                    ? '0 10px 25px rgba(124, 58, 237, 0.25)'
                                                    : '0 10px 25px rgba(16, 185, 129, 0.25)'
                                            }}
                                        >
                                            <div className="row align-items-center">
                                                {formData.userRole === 'Investor' ? (
                                                    formData.investmentType === 'One-Time' ? (
                                                        <>
                                                            <div className="col-sm-6 border-right" style={{ borderColor: 'rgba(255,255,255,0.2) !important' }}>
                                                                <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '1px', opacity: 0.9, display: 'block' }}>
                                                                    INVESTMENT CAPITAL
                                                                </span>
                                                                <h2 className="font-weight-bold mb-0 mt-1" style={{ fontSize: '32px' }}>₹{(parseFloat(formData.loanAmount) || 0).toLocaleString()}</h2>
                                                                <span style={{ fontSize: '12px', opacity: 0.8 }}>Principal Lump Sum</span>
                                                            </div>
                                                            <div className="col-sm-6 pl-sm-4 mt-3 mt-sm-0">
                                                                <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '1px', opacity: 0.9, display: 'block' }}>
                                                                    MATURITY PAYOUT RETURN
                                                                </span>
                                                                <h2 className="font-weight-bold mb-0 mt-1" style={{ fontSize: '32px' }}>₹{calculation.total.toLocaleString()}</h2>
                                                                <span style={{ fontSize: '12px', opacity: 0.8 }}>
                                                                    Principal + Accrued Interest (₹{(calculation.total - (parseFloat(formData.loanAmount) || 0)).toLocaleString()})
                                                                </span>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="col-sm-6 border-right" style={{ borderColor: 'rgba(255,255,255,0.2) !important' }}>
                                                                <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '1px', opacity: 0.9, display: 'block' }}>
                                                                    INSTALLMENT DEPOSIT
                                                                </span>
                                                                <h2 className="font-weight-bold mb-0 mt-1" style={{ fontSize: '32px' }}>₹{calculation.emi.toLocaleString()}</h2>
                                                                <span style={{ fontSize: '12px', opacity: 0.8 }}>
                                                                    Per {formData.emiFrequency === 'Daily' ? 'Day' : formData.emiFrequency === 'Monthly' ? 'Month' : 'Year'} Installment
                                                                </span>
                                                            </div>
                                                            <div className="col-sm-6 pl-sm-4 mt-3 mt-sm-0">
                                                                <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '1px', opacity: 0.9, display: 'block' }}>
                                                                    TOTAL MATURITY RETURN
                                                                </span>
                                                                <h2 className="font-weight-bold mb-0 mt-1" style={{ fontSize: '32px' }}>₹{calculation.total.toLocaleString()}</h2>
                                                                <span style={{ fontSize: '12px', opacity: 0.8 }}>
                                                                    Capital + Accrued Interest (₹{(calculation.total - (parseFloat(formData.loanAmount) || 0)).toLocaleString()})
                                                                </span>
                                                            </div>
                                                        </>
                                                    )
                                                ) : formData.emiFrequency === 'Flexible' ? (
                                                    <>
                                                        <div className="col-sm-6 border-right" style={{ borderColor: 'rgba(255,255,255,0.2) !important' }}>
                                                            <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '1px', opacity: 0.9, display: 'block' }}>
                                                                FLEXIBLE OPEN KHATA
                                                            </span>
                                                            <h2 className="font-weight-bold mb-0 mt-1" style={{ fontSize: '32px' }}>₹{calculation.total.toLocaleString()}</h2>
                                                            <span style={{ fontSize: '12px', opacity: 0.8 }}>
                                                                Includes 1st Month Fixed Interest
                                                            </span>
                                                        </div>
                                                        <div className="col-sm-6 pl-sm-4 mt-3 mt-sm-0">
                                                            <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '1px', opacity: 0.9, display: 'block' }}>
                                                                PENALTY AFTER 30 DAYS
                                                            </span>
                                                            <h2 className="font-weight-bold mb-0 mt-1" style={{ fontSize: '32px' }}>Day-wise</h2>
                                                            <span style={{ fontSize: '12px', opacity: 0.8 }}>
                                                                Interest adds up daily after a month
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="col-sm-6 border-right" style={{ borderColor: 'rgba(255,255,255,0.2) !important' }}>
                                                            <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '1px', opacity: 0.9, display: 'block' }}>
                                                                CALCULATED {formData.emiFrequency.toUpperCase()} EMI
                                                            </span>
                                                            <h2 className="font-weight-bold mb-0 mt-1" style={{ fontSize: '32px' }}>₹{calculation.emi.toLocaleString()}</h2>
                                                            <span style={{ fontSize: '12px', opacity: 0.8 }}>
                                                                Per {formData.emiFrequency === 'Daily' ? 'Day' : formData.emiFrequency === 'Monthly' ? 'Month' : 'Year'} Collection
                                                            </span>
                                                        </div>
                                                        <div className="col-sm-6 pl-sm-4 mt-3 mt-sm-0">
                                                            <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '1px', opacity: 0.9, display: 'block' }}>
                                                                TOTAL EXPECTED REPAYMENT
                                                            </span>
                                                            <h2 className="font-weight-bold mb-0 mt-1" style={{ fontSize: '32px' }}>₹{calculation.total.toLocaleString()}</h2>
                                                            <span style={{ fontSize: '12px', opacity: 0.8 }}>
                                                                Principal + Accrued Premium
                                                            </span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="col-12 mb-2">
                                    <label htmlFor="remark_txt" style={labelStyle}>Ledger Terms / Internal Guarantees</label>
                                    <textarea id="remark_txt" name="remarks" className="form-control premium-input" rows="2" value={formData.remarks} onChange={handleChange} placeholder="Stipulate specific collection directives, local witnesses, or physical security backed assets..."></textarea>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 4: Comprehensive Audit Review & Final Action */}
                    {step === 4 && (
                        <div className="step-container animate__animated animate__fadeIn">
                            <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                                <div className="rounded p-2 mr-3" style={{ backgroundColor: '#F3E8FF', color: '#9333EA' }}>
                                    <span style={{ fontSize: '20px' }}>📋</span>
                                </div>
                                <div>
                                    <h4 className="font-weight-bold mb-0" style={{ color: '#0F172A' }}>Comprehensive Audit Verification</h4>
                                    <span style={{ fontSize: '13px', color: '#64748B' }}>Reviewing ALL filled inputs before committing records</span>
                                </div>
                            </div>

                            {/* Section 1: Personal Details Review */}
                            <div className="review-section">
                                <span className="section-badge" style={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}>
                                    👤 1. Personal Identity Attributes
                                </span>
                                <div className="row">
                                    <div className="col-md-4 col-sm-6 mb-3">
                                        <span style={reviewLabel}>{formData.userRole === 'Investor' ? 'Investor' : 'Borrower'} Structure</span>
                                        <span style={reviewValue}>{formData.userRole === 'Investor' ? 'Investor' : formData.loanCategory} Profile</span>
                                    </div>
                                    <div className="col-md-4 col-sm-6 mb-3">
                                        <span style={reviewLabel}>Full Legal Name</span>
                                        <span style={reviewValue}>{formData.firstName} {formData.lastName || ''}</span>
                                    </div>
                                    <div className="col-md-4 col-sm-6 mb-3">
                                        <span style={reviewLabel}>Date of Birth</span>
                                        <span style={reviewValue}>{formData.dob || 'Not Filled'}</span>
                                    </div>
                                    <div className="col-md-4 col-sm-6 mb-3">
                                        <span style={reviewLabel}>Father&apos;s Name</span>
                                        <span style={reviewValue}>{formData.fatherName || 'N/A'}</span>
                                    </div>
                                    <div className="col-md-4 col-sm-6 mb-3">
                                        <span style={reviewLabel}>Mother&apos;s Name</span>
                                        <span style={reviewValue}>{formData.motherName || 'N/A'}</span>
                                    </div>
                                    <div className="col-md-4 col-sm-6 mb-3">
                                        <span style={reviewLabel}>Mobile Contact</span>
                                        <span style={reviewValue}>{formData.phone || 'N/A'}</span>
                                    </div>
                                    <div className="col-md-4 col-sm-6 mb-3">
                                        <span style={reviewLabel}>Email Address</span>
                                        <span style={reviewValue}>{formData.email || 'None'}</span>
                                    </div>
                                    <div className="col-md-8 col-sm-6 mb-3">
                                        <span style={reviewLabel}>Region / Jurisdiction</span>
                                        <span style={reviewValue}>{formData.city || 'City'}, {formData.state || 'State'} - {formData.zipCode || 'Zip'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Government KYC, Employment & Uploaded Photos View */}
                            <div className="review-section">
                                <span className="section-badge" style={{ backgroundColor: '#FEF3C7', color: '#B45309' }}>
                                    🛡️ 2. Government KYC & Uploaded Credentials
                                </span>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <span style={reviewLabel}>Aadhar Card Identity Number</span>
                                        <span style={reviewValue} className="text-primary font-weight-bold">{formData.aadhar || 'Not Provided'}</span>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <span style={reviewLabel}>PAN Income Identity Key</span>
                                        <span style={reviewValue} className="text-primary font-weight-bold">{formData.pan.toUpperCase() || 'Not Provided'}</span>
                                    </div>

                                    {/* Embedded Visual Previews side-by-side */}
                                    <div className="col-12 mt-2 mb-3">
                                        <div className="row">
                                            <div className="col-sm-6 mb-2">
                                                <span style={{ ...reviewLabel, color: '#0369A1' }}>Attached Aadhar Card Verification Photo</span>
                                                {formData.aadharImageBase64 ? (
                                                    <img src={formData.aadharImageBase64} alt="Aadhar Audit" className="img-preview-card mt-1 shadow-sm" style={{ maxHeight: '140px' }} />
                                                ) : (
                                                    <span className="text-muted small font-italic">No photo uploaded for Aadhar card</span>
                                                )}
                                            </div>
                                            <div className="col-sm-6 mb-2">
                                                <span style={{ ...reviewLabel, color: '#0369A1' }}>Attached PAN Card Verification Photo</span>
                                                {formData.panImageBase64 ? (
                                                    <img src={formData.panImageBase64} alt="PAN Audit" className="img-preview-card mt-1 shadow-sm" style={{ maxHeight: '140px' }} />
                                                ) : (
                                                    <span className="text-muted small font-italic">No photo uploaded for PAN card</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-4 col-sm-6 mb-3 border-top pt-2 mt-1">
                                        <span style={reviewLabel}>Occupation Sector</span>
                                        <span style={reviewValue}>{formData.occupation || 'N/A'}</span>
                                    </div>
                                    <div className="col-md-4 col-sm-6 mb-3 border-top pt-2 mt-1">
                                        <span style={reviewLabel}>Company / Workplace</span>
                                        <span style={reviewValue}>{formData.companyName || 'N/A'}</span>
                                    </div>
                                    <div className="col-md-4 col-sm-6 mb-3 border-top pt-2 mt-1">
                                        <span style={reviewLabel}>Monthly Base Revenue</span>
                                        <span style={reviewValue} className="text-success">₹{formData.income || '0'}</span>
                                    </div>
                                    <div className="col-12 mb-1">
                                        <span style={reviewLabel}>Full Registered Local Address</span>
                                        <span style={reviewValue}>{formData.address || 'No specific residential address captured'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Section 3: Bank Remittance Details */}
                            <div className="review-section">
                                <span className="section-badge" style={{ backgroundColor: '#E2E8F0', color: '#334155' }}>
                                    🏦 3. Registered Banking Coordinates
                                </span>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <span style={reviewLabel}>Authorized Holder Name</span>
                                        <span style={reviewValue}>{formData.accountHolderName || 'Same as Profile'}</span>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <span style={reviewLabel}>Bank Institution</span>
                                        <span style={reviewValue}>{formData.bankName || 'General System Bank'}</span>
                                    </div>
                                    <div className="col-md-6 mb-2">
                                        <span style={reviewLabel}>Account Number</span>
                                        <span className="text-dark font-weight-bold" style={{ ...reviewValue, letterSpacing: '1px', fontSize: '16px' }}>{formData.bankAccount || 'Not Provided'}</span>
                                    </div>
                                    <div className="col-md-6 mb-2">
                                        <span style={reviewLabel}>IFSC Interbank Key</span>
                                        <span style={reviewValue} className="text-uppercase">{formData.ifscCode || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Section 4: Loan Matrix Parameters */}
                            <div className="review-section" style={{ backgroundColor: '#ECFDF5', borderColor: '#A7F3D0' }}>
                                <span className="section-badge" style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>
                                    💰 4. Loan Capital & Schedule Metrics
                                </span>
                                <div className="row align-items-center">
                                    <div className="col-md-3 col-sm-6 mb-3 border-right" style={{ borderColor: '#D1FAE5' }}>
                                        <span style={reviewLabel}>PRINCIPAL {formData.userRole === 'Investor' ? 'INVESTMENT' : 'LOAN'}</span>
                                        <strong style={{ fontSize: '20px', color: '#065F46' }}>₹{formData.loanAmount || '0'}</strong>
                                    </div>
                                    <div className="col-md-3 col-sm-6 mb-3 border-right" style={{ borderColor: '#D1FAE5' }}>
                                        <span style={reviewLabel}>INTEREST RATE</span>
                                        <strong style={{ fontSize: '20px', color: '#065F46' }}>{formData.interestRate || '0'}% p.a.</strong>
                                    </div>
                                    <div className="col-md-3 col-sm-6 mb-3 border-right" style={{ borderColor: '#D1FAE5' }}>
                                        <span style={reviewLabel}>{formData.emiFrequency === 'Maturity' ? 'ONE-TIME MATURITY' : formData.emiFrequency.toUpperCase()} {formData.userRole === 'Investor' ? 'PAYOUT' : 'SCHEDULE'}</span>
                                        <strong style={{ fontSize: '20px', color: '#10B981' }}>₹{calculation.emi}</strong>
                                    </div>
                                    <div className="col-md-3 col-sm-6 mb-3">
                                        <span style={reviewLabel}>TOTAL DURATION</span>
                                        <strong style={{ fontSize: '18px', color: '#0F172A' }}>{formData.durationValue} {formData.emiFrequency === 'Daily' ? 'Days' : formData.emiFrequency === 'Monthly' ? 'Months' : formData.emiFrequency === 'Yearly' ? 'Years' : 'Months (to Maturity)'}</strong>
                                    </div>
                                    <div className="col-md-6 mb-2 mt-2 pt-2 border-top" style={{ borderColor: '#D1FAE5' }}>
                                        <span style={reviewLabel}>Mapped Start Date</span>
                                        <span style={reviewValue}>{formData.collectionStartDate || 'Immediate upon submission'}</span>
                                    </div>
                                    <div className="col-md-6 mb-2 mt-2 pt-2 border-top" style={{ borderColor: '#D1FAE5' }}>
                                        <span style={reviewLabel}>Calculated Gross {formData.userRole === 'Investor' ? 'Returns' : 'Payback'}</span>
                                        <strong style={{ fontSize: '16px', color: '#047857' }}>₹{calculation.total.toLocaleString()}</strong>
                                    </div>
                                    {formData.remarks && (
                                        <div className="col-12 mt-2 pt-2 border-top" style={{ borderColor: '#D1FAE5' }}>
                                            <span style={reviewLabel}>Stipulated Remarks</span>
                                            <span style={reviewValue} className="font-italic">{formData.remarks}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Explicit Prominent Verification Checkbox / Checkout Actions */}
                            <div className="p-4 mt-4" style={{ background: 'linear-gradient(to right, #FFFBEB, #FEF3C7)', borderRadius: '16px', border: '2px solid #F59E0B', boxShadow: '0 4px 15px rgba(245, 158, 11, 0.15)' }}>
                                <div className="d-flex align-items-center mb-2">
                                    <span style={{ fontSize: '22px' }} className="mr-2">⚠️</span>
                                    <h6 className="font-weight-bold mb-0" style={{ color: '#92400E', fontSize: '15px' }}>Mandatory Final Declaration Checklist</h6>
                                </div>
                                <div className="custom-control custom-checkbox pt-2">
                                    <input 
                                        type="checkbox" 
                                        className="custom-control-input" 
                                        id="checkoutConfirmCheck" 
                                        checked={isDeclared}
                                        onChange={(e) => setIsDeclared(e.target.checked)}
                                    />
                                    <label htmlFor="checkoutConfirmCheck" className="custom-control-label font-weight-bold pl-2" style={{ color: '#1E293B', fontSize: '15px', cursor: 'pointer', userSelect: 'none' }}>
                                        ☑️ I hereby verify and confirm that ALL the user details provided above are absolutely true, physically matched, and correctly configured. I authorize immediate addition of this {formData.userRole === 'Investor' ? 'investor' : 'borrower'} ledger.
                                    </label>
                                </div>
                            </div>

                            {/* Agreement Download & Upload Box */}
                            {isDeclared && (
                                <div className="p-4 mt-4 animate__animated animate__fadeIn" style={{ background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)' }}>
                                    <div className="d-flex align-items-center mb-3">
                                        <span style={{ fontSize: '22px' }} className="mr-2">📄</span>
                                        <h6 className="font-weight-bold mb-0" style={{ color: '#0F172A', fontSize: '15px' }}>Agreement Generation & Signed Copy Upload</h6>
                                    </div>
                                    <p className="text-muted small mb-3">
                                        Generate the professional agreement containing borrower/investor information, loan metrics, and legal clauses. Print/Download the PDF, obtain physical signatures, and upload the signed document scan to complete onboarding.
                                    </p>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <div className="p-3 text-center" style={{ border: '1px dashed #CBD5E1', borderRadius: '12px', background: '#FFFFFF' }}>
                                                <div className="mb-2" style={{ fontSize: '24px' }}>⬇️</div>
                                                <button 
                                                    type="button" 
                                                    className="btn btn-outline-primary font-weight-bold px-4 py-2" 
                                                    onClick={handleDownloadAgreement}
                                                >
                                                    Download Agreement PDF
                                                </button>
                                                <div className="text-muted small mt-2">Generates legal draft of terms and financial parameters</div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className="p-3 text-center" style={{ border: '1px dashed #CBD5E1', borderRadius: '12px', background: '#FFFFFF', position: 'relative' }}>
                                                <div className="mb-2" style={{ fontSize: '24px' }}>📤</div>
                                                <label className="btn btn-primary font-weight-bold px-4 py-2 mb-0" style={{ cursor: 'pointer' }}>
                                                    {uploadingAgreement ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                                            Uploading...
                                                        </>
                                                    ) : agreementPdfUrl ? (
                                                        "Re-upload Signed Copy"
                                                    ) : (
                                                        "Upload Signed Copy"
                                                    )}
                                                    <input 
                                                        type="file" 
                                                        accept="application/pdf,image/*" 
                                                        style={{ display: 'none' }} 
                                                        onChange={handleUploadAgreement}
                                                        disabled={uploadingAgreement}
                                                    />
                                                </label>
                                                <div className="text-muted small mt-2">Upload scanned PDF or Image of signed agreement</div>
                                                {agreementPdfUrl && (
                                                    <div className="mt-2 text-success font-weight-bold small d-flex align-items-center justify-content-center">
                                                        <span>✅ Signed agreement uploaded successfully! </span>
                                                        <a href={buildAgreementDocumentUrl(agreementPdfUrl)} target="_blank" rel="noopener noreferrer" className="ml-2 btn btn-xs btn-link p-0 font-weight-bold text-decoration-none">
                                                            View Document
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 5: Success Confirmed Component */}
                    {step === 5 && (
                        <div className="text-center py-5 animate__animated animate__zoomIn">
                            <div className="d-inline-flex align-items-center justify-content-center mb-4" style={{ width: '90px', height: '90px', borderRadius: '50%', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white', boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)' }}>
                                <span style={{ fontSize: '45px' }}>✓</span>
                            </div>
                            <h3 className="font-weight-bold" style={{ color: '#0F172A' }}>{formData.userRole === 'Investor' ? 'Investor' : 'Borrower'} Successfully Onboarded!</h3>
                            <p className="mx-auto mt-2" style={{ maxWidth: '500px', color: '#64748B', fontSize: '15px' }}>
                                The primary customer identity ledger, verified government KYC assets, and automated {formData.userRole === 'Investor' ? 'investment' : 'loan'} configuration parameters have been provisioned live.
                            </p>
                            
                            <div className="premium-card p-4 mx-auto my-4 text-left" style={{ maxWidth: '480px', backgroundColor: '#F8FAFC', borderLeft: '4px solid #10B981' }}>
                                <span style={{ fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', display: 'block' }}>Registered Target</span>
                                <strong style={{ fontSize: '18px', color: '#0F172A', display: 'block' }}>{formData.firstName} {formData.lastName}</strong>
                                <span style={{ fontSize: '13px', color: '#10B981', fontWeight: '600' }}>● Status Active Ledger Provisioned</span>
                            </div>

                            <button 
                                type="button"
                                onClick={handleResetForm}
                                className="btn font-weight-bold mt-2 shadow-sm"
                                style={{ background: 'linear-gradient(135deg, #0284C7 0%, #0369A1 100%)', color: 'white', borderRadius: '14px', padding: '14px 32px', fontSize: '16px' }}
                            >
                                ⊕ Onboard Another New {formData.userRole === 'Investor' ? 'Investor' : 'Borrower'}
                            </button>
                        </div>
                    )}

                    {/* Interactive Action Controls */}
                    {step < 5 && (
                        <div className="d-flex justify-content-between mt-5 pt-4 border-top">
                            {step > 1 ? (
                                <button 
                                    type="button" 
                                    className="btn px-4 py-3 font-weight-bold" 
                                    style={{ backgroundColor: '#F1F5F9', color: '#475569', borderRadius: '12px', fontSize: '15px' }} 
                                    onClick={handlePrev}
                                >
                                    ← Back to Previous Step
                                </button>
                            ) : <div />}

                            {step < 4 ? (
                                <button 
                                    type="button" 
                                    className="btn px-5 py-3 font-weight-bold shadow-sm" 
                                    style={{ background: 'linear-gradient(135deg, #0284C7 0%, #0369A1 100%)', color: 'white', borderRadius: '12px', fontSize: '16px' }} 
                                    onClick={handleNext}
                                >
                                    Proceed to Next Stage →
                                </button>
                            ) : (
                                <button 
                                    type="submit" 
                                    className="btn px-5 py-3 font-weight-bold shadow-lg" 
                                    disabled={loading || !isDeclared}
                                    style={{ 
                                        background: loading || !isDeclared ? '#94A3B8' : 'linear-gradient(135deg, #10B981 0%, #059669 100%)', 
                                        color: 'white', 
                                        borderRadius: '12px', 
                                        fontSize: '17px',
                                        padding: '14px 36px',
                                        transition: 'all 0.3s'
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                                            Adding Borrower Record...
                                        </>
                                    ) : (
                                        '✓ Finalize & Add User'
                                    )}
                                </button>
                            )}
                        </div>
                    )}

                </form>
            </div>
        </div>
    );
};

export default AddUser;
